/**
 * Motion cart bridge — live capacity enforce for drawer + cart page.
 * Mirrors CA WorkshopCityCartLive / WorkshopCartCapacityGuard without Eurus Alpine.
 */
(function (global) {
  'use strict';

  if (global.__MotionWorkshopCartBridgeInstalled) return;
  global.__MotionWorkshopCartBridgeInstalled = true;

  var DEFAULT_MESSAGE =
    'This workshop no longer has enough available seats. Please reduce the quantity or choose another event.';
  var SOLD_OUT_MESSAGE = 'This session is sold out.';
  var UNAVAILABLE_MESSAGE = 'This session is no longer available.';
  var VERIFY_MESSAGE = 'We could not verify workshop capacity right now. Please try again.';
  var BANNER_ATTR = 'data-wb-cart-capacity-block';
  var pollTimer = null;
  var cartBlocked = false;
  var blockMessage = '';

  function shopDomain() {
    return global.Shopify && global.Shopify.shop ? String(global.Shopify.shop) : '';
  }

  function cartRoot() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  function cityEvents() {
    return global.WorkshopCityEvents || null;
  }

  function bookingConfig() {
    return global.WorkshopBookingConfig || global.WorkshopCityConfig || null;
  }

  function configRoot() {
    return {
      getAttribute: function (name) {
        if (name !== 'data-section-id') return '';
        return document.getElementById('WcfConfig-cart-page') ? 'cart-page' : 'cart-drawer';
      },
    };
  }

  function validateGateEnabled(intent) {
    var CFG = bookingConfig();
    if (CFG && typeof CFG.validateGateEnabled === 'function') {
      return CFG.validateGateEnabled(configRoot(), intent);
    }
    if (intent === 'update_line') return false;
    if (intent === 'checkout') return true;
    return true;
  }

  function prop(properties, key) {
    if (!properties) return '';
    if (!Array.isArray(properties)) {
      return properties[key] != null ? String(properties[key]) : '';
    }
    for (var i = 0; i < properties.length; i++) {
      var entry = properties[i];
      if (!entry) continue;
      var k = entry[0] != null ? entry[0] : entry.name;
      var v = entry[1] != null ? entry[1] : entry.value;
      if (k === key && v != null) return String(v);
    }
    return '';
  }

  function isWorkshopLine(item) {
    var p = item && item.properties;
    return prop(p, '_workshop') === 'true' || prop(p, '_is_workshop') === '1';
  }

  function isAddonLine(item) {
    return prop(item && item.properties, '_workshop_addon') === 'true';
  }

  function eventIdForItem(item) {
    var p = item && item.properties;
    return prop(p, '_event_id') || prop(p, 'event_id') || prop(p, '_event_metaobject_gid');
  }

  function eventAliases(item) {
    var api = cityEvents();
    var raw = [];
    ['_event_id', 'event_id', '_event_metaobject_gid'].forEach(function (key) {
      var value = prop(item && item.properties, key);
      if (value) raw.push(value);
    });
    if (api && typeof api.eventIdAliases === 'function') {
      var out = [];
      raw.forEach(function (value) {
        api.eventIdAliases(value).forEach(function (alias) {
          if (out.indexOf(alias) === -1) out.push(alias);
        });
      });
      return out;
    }
    return raw;
  }

  function aliasesMatch(a, b) {
    var api = cityEvents();
    if (api && typeof api.liveStateAliasesMatch === 'function') {
      return api.liveStateAliasesMatch(a, b);
    }
    return String(a || '') === String(b || '');
  }

  function fetchCart() {
    return fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
      function (res) {
        return res.ok ? res.json() : null;
      }
    );
  }

  function collectEventGids(cart) {
    var gids = [];
    if (!cart || !Array.isArray(cart.items)) return gids;
    cart.items.forEach(function (item) {
      if (!isWorkshopLine(item) || isAddonLine(item)) return;
      var gid = eventIdForItem(item);
      if (!gid) return;
      var api = cityEvents();
      var primary = api && typeof api.primaryEventIdForRequest === 'function' ? api.primaryEventIdForRequest(gid) : gid;
      var duplicate = gids.some(function (existing) {
        return aliasesMatch(existing, primary);
      });
      if (!duplicate) gids.push(primary || gid);
    });
    return gids;
  }

  function seatsForEvent(cart, eventGid, excludeKey) {
    var total = 0;
    if (!cart || !Array.isArray(cart.items) || !eventGid) return 0;
    cart.items.forEach(function (item) {
      if (!isWorkshopLine(item) || isAddonLine(item)) return;
      if (excludeKey && item.key === excludeKey) return;
      var aliases = eventAliases(item);
      var match = aliases.some(function (alias) {
        return aliasesMatch(alias, eventGid);
      });
      if (match) total += Math.max(0, Number(item.quantity) || 0);
    });
    return total;
  }

  function normalizeStatus(status) {
    if (status == null || status === '') return '';
    return String(status).toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  }

  function isBlockingVerdict(verdict) {
    var eventStatus = normalizeStatus(verdict && (verdict.event_status || verdict.status));
    var displayStatus = normalizeStatus(verdict && verdict.display_status);
    var cap = verdict && verdict.capacity_remaining;
    return (
      eventStatus === 'draft' ||
      eventStatus === 'completed' ||
      eventStatus === 'cancelled' ||
      eventStatus === 'sold_out' ||
      displayStatus === 'hidden' ||
      displayStatus === 'ended' ||
      displayStatus === 'sold_out' ||
      (cap != null && cap !== '' && Number(cap) <= 0)
    );
  }

  function isSoldOutStatus(status, displayStatus) {
    var st = normalizeStatus(status);
    var ds = normalizeStatus(displayStatus);
    return st === 'sold_out' || ds === 'sold_out';
  }

  /** Prefer explicit sold-out copy when listing/status says sold_out (even if seats left > 0). */
  function messageForStatuses(status, displayStatus, fallback) {
    if (isSoldOutStatus(status, displayStatus)) return SOLD_OUT_MESSAGE;
    var st = normalizeStatus(status);
    var ds = normalizeStatus(displayStatus);
    if (
      st === 'draft' ||
      st === 'completed' ||
      st === 'cancelled' ||
      ds === 'hidden' ||
      ds === 'ended'
    ) {
      return UNAVAILABLE_MESSAGE;
    }
    return fallback || DEFAULT_MESSAGE;
  }

  function verdictMessage(verdict) {
    if (!verdict) return DEFAULT_MESSAGE;
    return messageForStatuses(
      verdict.event_status || verdict.status,
      verdict.display_status,
      verdict.message || DEFAULT_MESSAGE
    );
  }

  function validateGroup(eventId, totalQty, intent) {
    var api = cityEvents();
    if (!api || typeof api.validateCapacity !== 'function') {
      return Promise.resolve({ allowed: false, message: VERIFY_MESSAGE, validation_unavailable: true });
    }
    return api
      .validateCapacity(eventId, totalQty, { intent: intent || 'checkout' })
      .then(function (verdict) {
        if (!verdict) {
          return { allowed: false, message: VERIFY_MESSAGE, validation_unavailable: true };
        }
        if (verdict.validation_unavailable === true) {
          return {
            allowed: false,
            message: verdict.message || VERIFY_MESSAGE,
            validation_unavailable: true,
            capacity_remaining: verdict.capacity_remaining,
          };
        }
        var blocked = isBlockingVerdict(verdict);
        return {
          allowed: !!(verdict.allowed === true && !blocked),
          message: messageForStatuses(
            verdict.event_status || verdict.status,
            verdict.display_status,
            verdict.message || (blocked ? UNAVAILABLE_MESSAGE : '')
          ),
          capacity_remaining: verdict.capacity_remaining,
          display_status: verdict.display_status,
          event_status: verdict.event_status || verdict.status,
          status: verdict.status,
          authoritative: verdict.authoritative,
          blocked: blocked,
          validation_unavailable: false,
        };
      })
      .catch(function () {
        return { allowed: false, message: VERIFY_MESSAGE, validation_unavailable: true };
      });
  }

  function groupWorkshopLines(cart, overrideKey, overrideQty) {
    var groups = {};
    var aliasToKey = {};
    if (!cart || !Array.isArray(cart.items)) return groups;
    cart.items.forEach(function (item) {
      if (!isWorkshopLine(item) || isAddonLine(item)) return;
      var aliases = eventAliases(item);
      if (!aliases.length) return;
      var existingAlias = aliases.find(function (alias) {
        return aliasToKey[alias];
      });
      var groupKey = existingAlias ? aliasToKey[existingAlias] : aliases[0];
      if (!groups[groupKey]) {
        groups[groupKey] = { eventId: eventIdForItem(item) || groupKey, aliases: [], total: 0 };
      }
      aliases.forEach(function (alias) {
        if (groups[groupKey].aliases.indexOf(alias) === -1) groups[groupKey].aliases.push(alias);
        aliasToKey[alias] = groupKey;
      });
      var qty =
        overrideKey && item.key === overrideKey
          ? Math.max(0, Math.floor(Number(overrideQty) || 0))
          : Math.max(0, Number(item.quantity) || 0);
      groups[groupKey].total += qty;
    });
    return groups;
  }

  function validateCart(cart, intent) {
    intent = intent || 'checkout';
    if (intent === 'update_line' && !validateGateEnabled('update_line')) {
      return Promise.resolve({ allowed: true, cart: cart });
    }
    if (intent === 'checkout' && !validateGateEnabled('checkout')) {
      return Promise.resolve({ allowed: true, cart: cart });
    }
    var load = cart ? Promise.resolve(cart) : fetchCart();
    return load.then(function (cartData) {
      if (!cartData) return { allowed: false, message: VERIFY_MESSAGE, validation_unavailable: true };
      var groups = groupWorkshopLines(cartData);
      var keys = Object.keys(groups);
      if (!keys.length) return { allowed: true, cart: cartData };

      var chain = Promise.resolve({ allowed: true, cart: cartData });
      keys.forEach(function (key) {
        chain = chain.then(function (prev) {
          if (!prev.allowed) return prev;
          var group = groups[key];
          var aliases = [];
          if (group.eventId) aliases.push(group.eventId);
          (group.aliases || []).forEach(function (alias) {
            if (alias && aliases.indexOf(alias) === -1) aliases.push(alias);
          });
          var attempt = Promise.resolve(null);
          aliases.forEach(function (alias) {
            attempt = attempt.then(function (accepted) {
              if (accepted && accepted.allowed) return accepted;
              return validateGroup(alias, group.total || 0, intent).then(function (verdict) {
                if (verdict && verdict.allowed) return verdict;
                return accepted || verdict;
              });
            });
          });
          return attempt.then(function (verdict) {
            if (verdict && verdict.validation_unavailable) {
              return {
                allowed: false,
                message: (verdict && verdict.message) || VERIFY_MESSAGE,
                cart: cartData,
                eventId: group.eventId,
                validation_unavailable: true,
              };
            }
            if (!verdict || verdict.allowed !== true) {
              return {
                allowed: false,
                message: verdictMessage(verdict),
                cart: cartData,
                eventId: group.eventId,
                validation_unavailable: false,
              };
            }
            return { allowed: true, cart: cartData };
          });
        });
      });
      return chain;
    });
  }

  function maxQtyForLine(cart, item, liveCap) {
    if (liveCap == null || liveCap === '' || !Number.isFinite(Number(liveCap))) return null;
    var remaining = Math.max(0, Math.floor(Number(liveCap)));
    var others = seatsForEvent(cart, eventIdForItem(item), item.key);
    return Math.max(0, remaining - others);
  }

  /** Banner/deny copy: session remaining capacity + cart total for that event (not per-line max). */
  function overCapacityMessage(cart, item, liveCap, lineQty) {
    var remaining = Math.max(0, Math.floor(Number(liveCap)));
    if (!(remaining > 0)) return 'This session is sold out.';
    var qty = lineQty != null ? Math.max(0, Math.floor(Number(lineQty) || 0)) : Math.max(0, Number(item.quantity) || 0);
    var total = seatsForEvent(cart, eventIdForItem(item), item.key) + qty;
    return (
      'Your cart total for this session (' +
      total +
      ') exceeds remaining capacity. Only ' +
      remaining +
      ' seat(s) remain — reduce the total to ' +
      remaining +
      ' or fewer.'
    );
  }

  function liveStateForItem(item, liveMap) {
    if (!liveMap || !item) return null;
    var aliases = eventAliases(item);
    var keys = Object.keys(liveMap);
    var i;
    var k;
    for (i = 0; i < aliases.length; i++) {
      if (liveMap[aliases[i]]) return liveMap[aliases[i]];
      if (liveMap[String(aliases[i])]) return liveMap[String(aliases[i])];
    }
    for (i = 0; i < aliases.length; i++) {
      for (k = 0; k < keys.length; k++) {
        if (aliasesMatch(aliases[i], keys[k])) return liveMap[keys[k]];
      }
    }
    return null;
  }

  function liveCapForItem(item, liveMap) {
    var state = liveStateForItem(item, liveMap);
    if (!state || state.capacity_remaining == null || state.capacity_remaining === '') return null;
    var n = Number(state.capacity_remaining);
    return Number.isFinite(n) ? n : null;
  }

  function primaryEventGid(item) {
    var gid = eventIdForItem(item);
    if (!gid) return '';
    var api = cityEvents();
    if (api && typeof api.primaryEventIdForRequest === 'function') {
      return api.primaryEventIdForRequest(gid) || gid;
    }
    return gid;
  }

  function refreshLive(cart, options) {
    options = options || {};
    var api = cityEvents();
    var gids = options.gids && options.gids.length ? options.gids : collectEventGids(cart);
    if (!api || typeof api.fetchLiveState !== 'function' || !gids.length) {
      return Promise.resolve({ ok: true, states: {}, fetchAttempted: false });
    }
    return api.fetchLiveState(gids, { root: configRoot(), applyMeta: options.applyMeta !== false }).then(
      function (result) {
        return {
          ok: !!(result && result.ok),
          states: (result && result.states) || {},
          as_of: result && result.as_of,
          fetchAttempted: true,
        };
      }
    );
  }

  /**
   * Soft banner = verify/network unknown — warn but do NOT lock checkout.
   * Hard banner = known over-capacity / sold-out / missing event id.
   */
  function showBanner(message, options) {
    options = options || {};
    var soft = options.soft === true;
    blockMessage = message || DEFAULT_MESSAGE;
    cartBlocked = !soft;
    var targets = [
      document.getElementById('CartDrawer'),
      document.getElementById('CartPageForm'),
      document.querySelector('[data-products]'),
    ].filter(Boolean);
    var roots = [];
    targets.forEach(function (el) {
      var root = el.closest('form') || el;
      if (roots.indexOf(root) === -1) roots.push(root);
    });
    if (!roots.length) roots.push(document.body);
    roots.forEach(function (root) {
      var banner = root.querySelector('[' + BANNER_ATTR + ']');
      if (!banner) {
        banner = document.createElement('div');
        banner.setAttribute(BANNER_ATTR, '');
        banner.setAttribute('role', 'alert');
        banner.style.cssText =
          'margin:0 0 12px;padding:12px 14px;border-radius:10px;background:#fef2f2;color:#991b1b;border:1px solid #fecaca;font-size:14px;line-height:1.45;';
        var anchor =
          root.querySelector('.cart__checkout-wrapper') ||
          root.querySelector('.drawer__footer') ||
          root.querySelector('[data-products]') ||
          root.firstElementChild;
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(banner, anchor);
        else root.prepend(banner);
      }
      banner.textContent = blockMessage;
      banner.hidden = false;
      banner.style.background = soft ? '#fffbeb' : '#fef2f2';
      banner.style.color = soft ? '#92400e' : '#991b1b';
      banner.style.borderColor = soft ? '#fde68a' : '#fecaca';
    });
    setCheckoutDisabled(cartBlocked, cartBlocked ? blockMessage : '');
  }

  function hideBanner() {
    cartBlocked = false;
    blockMessage = '';
    document.querySelectorAll('[' + BANNER_ATTR + ']').forEach(function (el) {
      el.hidden = true;
    });
    setCheckoutDisabled(false, '');
  }

  function setCheckoutDisabled(disabled, title) {
    document.querySelectorAll('.cart__checkout').forEach(function (btn) {
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        if (title) btn.setAttribute('title', title);
      } else {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('title');
      }
    });
    document.querySelectorAll('.additional-checkout-buttons').forEach(function (el) {
      el.style.display = disabled ? 'none' : '';
    });
  }

  function denyLine(message, cart, maxQty) {
    showBanner(message);
    return { allowed: false, message: message, maxQty: maxQty, cart: cart };
  }

  function allowLine(cart) {
    return { allowed: true, cart: cart };
  }

  /**
   * When live capacity could not be read, ask the validate API before deciding.
   * - Explicit deny → hard block (real capacity problem)
   * - Explicit allow → allow (capacity exists; live map just missed the GID)
   * - Unavailable → allow for qty UI (do not kill the sale on unread / network blip)
   */
  function resolveWhenLiveUnread(item, cart, desiredTotal) {
    var eventId = primaryEventGid(item) || eventIdForItem(item);
    if (!eventId) {
      return Promise.resolve(
        denyLine('This workshop item is missing event details. Please remove it and book again.', cart)
      );
    }
    return validateGroup(eventId, desiredTotal, 'checkout').then(function (verdict) {
      if (verdict && verdict.validation_unavailable) {
        return allowLine(cart);
      }
      if (verdict && verdict.allowed === true) {
        return allowLine(cart);
      }
      return denyLine(verdictMessage(verdict), cart);
    });
  }

  function syncBannerFromCart(cart) {
    var load = cart ? Promise.resolve(cart) : fetchCart();
    return load
      .then(function (cartData) {
        if (!cartData || !Array.isArray(cartData.items)) {
          // Soft: cart.js hiccup must not freeze checkout forever.
          showBanner(VERIFY_MESSAGE, { soft: true });
          return true;
        }
        return refreshLive(cartData).then(function (live) {
          var states = (live && live.states) || {};
          var i;
          for (i = 0; i < cartData.items.length; i++) {
            var item = cartData.items[i];
            if (!isWorkshopLine(item) || isAddonLine(item)) continue;
            if (!eventIdForItem(item)) {
              showBanner('This workshop item is missing event details. Please remove it and book again.');
              return false;
            }
            var liveState = liveStateForItem(item, states);
            if (
              liveState &&
              isSoldOutStatus(liveState.status, liveState.display_status)
            ) {
              showBanner(SOLD_OUT_MESSAGE);
              return false;
            }
            var cap = liveCapForItem(item, states);
            if (cap != null && Number.isFinite(cap)) {
              var maxForLine = maxQtyForLine(cartData, item, cap);
              if (maxForLine != null && item.quantity > maxForLine) {
                showBanner(overCapacityMessage(cartData, item, cap, item.quantity));
                return false;
              }
            }
          }

          // Live unread / empty: prefer authoritative validate, not a hard soft-fail.
          if (!live || live.fetchAttempted !== true || live.ok !== true) {
            return validateCart(cartData, 'checkout').then(function (verdict) {
              if (verdict && verdict.validation_unavailable) {
                showBanner(VERIFY_MESSAGE, { soft: true });
                return true;
              }
              if (!verdict || verdict.allowed !== true) {
                showBanner(verdictMessage(verdict));
                return false;
              }
              hideBanner();
              return true;
            });
          }

          return validateCart(cartData, 'checkout').then(function (verdict) {
            if (verdict && verdict.validation_unavailable) {
              showBanner(VERIFY_MESSAGE, { soft: true });
              return true;
            }
            if (!verdict || verdict.allowed !== true) {
              showBanner(verdictMessage(verdict));
              return false;
            }
            hideBanner();
            return true;
          });
        });
      })
      .catch(function () {
        showBanner(VERIFY_MESSAGE, { soft: true });
        return true;
      });
  }

  function gateLineUpdate(key, qty) {
    return fetchCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) {
        // Qty change should not hard-fail the whole cart on a transient cart.js miss.
        return allowLine(cart);
      }
      var item = cart.items.find(function (entry) {
        return entry.key === key;
      });
      if (!item || !isWorkshopLine(item) || isAddonLine(item)) {
        return allowLine(cart);
      }

      var desired = Math.max(0, Math.floor(Number(qty) || 0));
      var currentQty = Math.max(0, Number(item.quantity) || 0);
      if (desired === 0 || desired <= currentQty) {
        return allowLine(cart);
      }

      var eventGid = primaryEventGid(item);
      // Central pattern: force-refresh THIS event (not whole-cart share race).
      return refreshLive(cart, { gids: eventGid ? [eventGid] : collectEventGids(cart), applyMeta: false }).then(
        function (live) {
          var states = (live && live.states) || {};
          var liveState = liveStateForItem(item, states);
          if (liveState && isSoldOutStatus(liveState.status, liveState.display_status)) {
            return denyLine(SOLD_OUT_MESSAGE, cart, 0);
          }
          var cap = liveCapForItem(item, states);
          var desiredTotal = seatsForEvent(cart, eventIdForItem(item), item.key) + desired;

          if (cap != null && Number.isFinite(cap)) {
            var maxForLine = maxQtyForLine(cart, item, cap);
            if (maxForLine != null && desired > maxForLine) {
              return denyLine(overCapacityMessage(cart, item, cap, desired), cart, maxForLine);
            }
            // Live number present and under cap — enough for qty gate.
            if (!validateGateEnabled('update_line')) {
              return allowLine(cart);
            }
            return validateGroup(eventGid || eventIdForItem(item), desiredTotal, 'update_line').then(function (
              verdict
            ) {
              if (verdict && verdict.validation_unavailable) return allowLine(cart);
              if (verdict && verdict.allowed === true) return allowLine(cart);
              return denyLine(verdictMessage(verdict), cart);
            });
          }

          // Live unread / missing GID after refresh: validate before blocking.
          return resolveWhenLiveUnread(item, cart, desiredTotal);
        }
      );
    });
  }

  function wrapChangeItem() {
    if (!global.theme || !global.theme.cart || typeof global.theme.cart.changeItem !== 'function') return;
    if (global.theme.cart.__wbCapacityWrapped) return;
    var original = global.theme.cart.changeItem.bind(global.theme.cart);
    global.theme.cart.__wbCapacityWrapped = true;
    global.theme.cart.changeItem = function (key, qty) {
      return gateLineUpdate(key, qty).then(function (result) {
        if (!result || result.allowed !== true) {
          document.querySelectorAll('.is-loading').forEach(function (el) {
            el.classList.remove('is-loading');
          });
          document.dispatchEvent(new CustomEvent('cart:build'));
          return Promise.reject(new Error((result && result.message) || DEFAULT_MESSAGE));
        }
        return original(key, qty).then(function (cart) {
          syncBannerFromCart(cart);
          return cart;
        });
      });
    };
  }

  function bindCheckoutForms() {
    function proceedCheckout(form, submitter) {
      hideBanner();
      form.__wbCapacitySubmitLocked = true;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
      else form.submit();
      window.setTimeout(function () {
        form.__wbCapacitySubmitLocked = false;
        if (submitter) submitter.classList.remove('btn--loading');
      }, 1500);
    }

    function onSubmit(evt) {
      var form = evt.currentTarget;
      if (!form || form.__wbCapacitySubmitLocked) return;
      var submitter = evt.submitter || form.querySelector('.cart__checkout');
      if (submitter && submitter.getAttribute('name') && submitter.getAttribute('name') !== 'checkout') return;
      // Only hard-block when we *know* capacity is bad, not on soft verify banners.
      if (cartBlocked) {
        evt.preventDefault();
        showBanner(blockMessage || DEFAULT_MESSAGE);
        if (submitter) submitter.classList.remove('btn--loading');
        return;
      }
      evt.preventDefault();
      if (submitter) submitter.classList.add('btn--loading');
      validateCart(null, 'checkout')
        .then(function (verdict) {
          if (verdict && verdict.validation_unavailable) {
            // Network/API unread — do not stop the sale; checkout Function is the hard SOT.
            showBanner(VERIFY_MESSAGE, { soft: true });
            proceedCheckout(form, submitter);
            return;
          }
          if (!verdict || verdict.allowed !== true) {
            showBanner(verdictMessage(verdict));
            if (submitter) submitter.classList.remove('btn--loading');
            return;
          }
          proceedCheckout(form, submitter);
        })
        .catch(function () {
          showBanner(VERIFY_MESSAGE, { soft: true });
          proceedCheckout(form, submitter);
        });
    }

    ['CartDrawerForm', 'CartPageForm'].forEach(function (id) {
      var form = document.getElementById(id);
      if (!form || form.__wbCapacityBound) return;
      form.__wbCapacityBound = true;
      form.addEventListener('submit', onSubmit, true);
    });
  }

  function pollIntervalMs() {
    var CFG = bookingConfig();
    if (CFG && typeof CFG.cartOpenPollIntervalMs === 'function') {
      var n = CFG.cartOpenPollIntervalMs(configRoot());
      if (n > 0) return n;
    }
    if (CFG && typeof CFG.pollIntervalMs === 'function') {
      var m = CFG.pollIntervalMs(configRoot(), 'browsing');
      if (m > 0) return m;
    }
    return 10000;
  }

  function startPoll() {
    stopPoll();
    var ms = pollIntervalMs();
    if (!(ms > 0)) return;
    pollTimer = global.setInterval(function () {
      fetchCart().then(syncBannerFromCart);
    }, ms);
  }

  function stopPoll() {
    if (pollTimer) {
      global.clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function onCartOpen() {
    wrapChangeItem();
    bindCheckoutForms();
    fetchCart().then(function (cart) {
      syncBannerFromCart(cart);
    });
    startPoll();
  }

  function onCartDrawerClosed() {
    // Cart page keeps polling; drawer-only sessions stop when closed.
    if (document.getElementById('CartPageForm')) return;
    stopPoll();
  }

  function watchCartDrawerClose() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer || drawer.__wbCapacityCloseWatch) return;
    drawer.__wbCapacityCloseWatch = true;
    var wasOpen = drawer.classList.contains('drawer--is-open');
    var observer = new MutationObserver(function () {
      var isOpen = drawer.classList.contains('drawer--is-open');
      if (wasOpen && !isOpen) onCartDrawerClosed();
      wasOpen = isOpen;
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function boot() {
    wrapChangeItem();
    bindCheckoutForms();
    watchCartDrawerClose();
    document.addEventListener('cart:open', onCartOpen);
    document.addEventListener('drawerOpen.CartDrawer', onCartOpen);
    document.addEventListener('cart:updated', function (evt) {
      var cart = evt && evt.detail && evt.detail.cart;
      if (cart) syncBannerFromCart(cart);
      else fetchCart().then(syncBannerFromCart);
    });
    document.addEventListener('ajaxProduct:added', function () {
      fetchCart().then(syncBannerFromCart);
    });
    if (document.getElementById('CartDrawerForm') || document.getElementById('CartPageForm')) {
      fetchCart().then(syncBannerFromCart);
    }
    // Cart page: keep a light poll while the page is open.
    if (document.getElementById('CartPageForm')) {
      startPoll();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  global.MotionWorkshopCartBridge = {
    validateCart: validateCart,
    gateLineUpdate: gateLineUpdate,
    syncBannerFromCart: syncBannerFromCart,
    showBanner: showBanner,
    hideBanner: hideBanner,
  };
})(typeof window !== 'undefined' ? window : this);
